import React, { useEffect, useState } from "react";
import { Pagination } from "antd";
import { useHistory, useLocation } from "react-router-dom";
import { cn } from "../../utils/cn";

const CommonPagination = ({
  currentPage: propCurrentPage,
  totalRecords,
  setCurrentPage,
  className,
  defaultLimit = 24, // default limit
  activePageRecords,
  paginationFor,
}) => {
  const history = useHistory();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const pageFromQuery = parseInt(searchParams.get("page") || "1", 10);
  const limitFromQuery = parseInt(
    searchParams.get("limit") || defaultLimit,
    10
  );

  const [page, setPage] = useState(pageFromQuery);
  const [limit, setLimit] = useState(limitFromQuery);

  // Sync state with URL query params
  useEffect(() => {
    const currentPageFromUrl = parseInt(searchParams.get("page") || "1", 10);
    const currentLimitFromUrl = parseInt(
      searchParams.get("limit") || defaultLimit,
      10
    );
    const currentOffsetFromUrl = parseInt(
      searchParams.get("offset") || "0",
      10
    );

    if (currentPageFromUrl !== page || currentLimitFromUrl !== limit) {
      setPage(currentPageFromUrl);
      setLimit(currentLimitFromUrl);

      setCurrentPage?.({
        page: currentPageFromUrl,
        limit: currentLimitFromUrl,
        offset: currentOffsetFromUrl,
      });
    }
  }, [location.search]);

  // Keep page in sync with prop
  useEffect(() => {
    if (propCurrentPage && propCurrentPage !== page) {
      setPage(propCurrentPage);
    }
  }, [propCurrentPage]);

  // Update URL query params
  const updateUrlParams = (newPage, newLimit, newOffset) => {
    const params = new URLSearchParams(location.search);
    params.set("page", newPage);
    params.set("limit", newLimit);
    params.set("offset", newOffset);
    history.push({
      pathname: location.pathname,
      search: params.toString(),
    });
  };

  // Page change handler
  const handlePageChange = (newPage, newPageSize) => {
    const newOffset = (newPage - 1) * newPageSize;
    setPage(newPage);
    setLimit(newPageSize);

    setCurrentPage?.({
      page: newPage,
      limit: newPageSize,
      offset: newOffset,
    });

    updateUrlParams(newPage, newPageSize, newOffset);
  };

  // Page size change handler
  const handlePageSizeChange = (_, newSize) => {
    const newOffset = 0; // reset offset
    setPage(1);
    setLimit(newSize);

    setCurrentPage?.({
      page: 1,
      limit: newSize,
      offset: newOffset,
    });

    updateUrlParams(1, newSize, newOffset);
  };

  return (
    <div
      className={cn(
        "flex justify-center items-center w-full flex-col space-y-2",
        className
      )}
    >
      <div>
        <span className="text-xs sm:text-sm text-secondary">
          Showing {activePageRecords} of {totalRecords} {paginationFor}
        </span>
      </div>
      <Pagination
        responsive={true}
        showLessItems
        showSizeChanger
        current={page}
        total={totalRecords}
        pageSize={limit}
        pageSizeOptions={["24", "48", "66", "96"]}
        onChange={handlePageChange}
        onShowSizeChange={handlePageSizeChange}
        selectProps={{ className: "!h-10 flex items-center" }}
        itemRender={(page, type, originalElement) => {
          const totalPages = Math.ceil(totalRecords / limit);
          const currentPage = propCurrentPage || 1;

          // Previous button
          if (type === "prev") {
            const isDisabled = currentPage === 1;
            return (
              <button
                className={`px-2 sm:!px-4 h-8 sm:h-10 mx-0.5 sm:mx-0.5 rounded-md text-white text-xs sm:text-sm font-medium transition-all duration-200 ${isDisabled
                  ? "bg-gray-300 cursor-not-allowed opacity-60"
                  : "bg-[#2dbdf2] hover:bg-[#25a5d8] cursor-pointer"
                  }`}
                disabled={isDisabled}
              >
                <span className="hidden xs:inline">← Prev</span>
                <span className="xs:hidden">←</span>
              </button>
            );
          }

          // Next button
          if (type === "next") {
            const isDisabled = currentPage >= totalPages;
            return (
              <button
                className={`px-2 sm:!px-4 h-8 sm:h-10 mx-0.5 sm:mx-0.5 rounded-md text-white text-xs sm:text-sm font-medium transition-all duration-200 ${isDisabled
                  ? "bg-gray-300 cursor-not-allowed opacity-60"
                  : "bg-[#2dbdf2] hover:bg-[#25a5d8] cursor-pointer"
                  }`}
                disabled={isDisabled}
              >
                <span className="hidden xs:inline">Next →</span>
                <span className="xs:hidden">→</span>
              </button>
            );
          }

          // Jump prev/next (...)
          if (type === "jump-prev" || type === "jump-next") {
            return originalElement;
          }

          // Page numbers
          const isActive = page === currentPage;
          return (
            <button
              className={`px-1.5 sm:px-3 h-8 sm:h-10 min-w-[32px] sm:min-w-[40px] rounded-md mx-0.5 sm:mx-0.5 text-xs sm:text-sm font-medium transition-all duration-200 border-none ${isActive
                ? "bg-[#2dbdf2] text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
            >
              {page}
            </button>
          );
        }}
      />
    </div>
  );
};

export default CommonPagination;
